import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Demo signature-walkthrough state machine. Holds ONLY animation/timing state
 * for the faked "watch a 2-of-2 signature complete" flow — no crypto, no
 * nonces, no relay. Kept separate from authSlice so animation churn doesn't
 * touch auth state.
 *
 * IMPORTANT: like `auth.isDemo`, this slice is NEVER persisted to localStorage
 * and NEVER cross-tab synced (see persistMiddleware.ts — it is intentionally
 * absent from every persist matcher and from setupCrossTabSync).
 */
export type WalkthroughStep =
  | 'idle'
  | 'requesting'
  | 'walletSigning'
  | 'keyPrompt'
  | 'keySigning'
  | 'assembling'
  | 'broadcasting'
  | 'confirmed';

export const WALKTHROUGH_STEP_ORDER: WalkthroughStep[] = [
  'idle',
  'requesting',
  'walletSigning',
  'keyPrompt',
  'keySigning',
  'assembling',
  'broadcasting',
  'confirmed',
];

interface DemoState {
  walkthroughStep: WalkthroughStep;
  // True while the timer-driven auto-advance is running. Skip/step-through
  // toggles set this false so the salesperson controls pacing manually.
  isPlaying: boolean;
  // When true, the user is driving each step manually ("Step through").
  isManual: boolean;
  // Demo-only fake artifacts, generated by a pure function (never crypto).
  walletSignature: string | null;
  keySignature: string | null;
  txHash: string | null;
}

const initialState: DemoState = {
  walkthroughStep: 'idle',
  isPlaying: false,
  isManual: false,
  walletSignature: null,
  keySignature: null,
  txHash: null,
};

const demoSlice = createSlice({
  name: 'demo',
  initialState,
  reducers: {
    setWalkthroughStep: (state, action: PayloadAction<WalkthroughStep>) => {
      state.walkthroughStep = action.payload;
    },
    setIsPlaying: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload;
    },
    setIsManual: (state, action: PayloadAction<boolean>) => {
      state.isManual = action.payload;
    },
    setDemoArtifacts: (
      state,
      action: PayloadAction<{
        walletSignature?: string | null;
        keySignature?: string | null;
        txHash?: string | null;
      }>
    ) => {
      if (action.payload.walletSignature !== undefined) {
        state.walletSignature = action.payload.walletSignature;
      }
      if (action.payload.keySignature !== undefined) {
        state.keySignature = action.payload.keySignature;
      }
      if (action.payload.txHash !== undefined) {
        state.txHash = action.payload.txHash;
      }
    },
    resetWalkthrough: () => initialState,
  },
});

export const { setWalkthroughStep, setIsPlaying, setIsManual, setDemoArtifacts, resetWalkthrough } =
  demoSlice.actions;

export default demoSlice.reducer;
